import React, { ChangeEvent, Suspense, useEffect, useMemo, useState } from 'react';
import { FormCheck, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import dateFormat from 'dateformat';

import { NoDataComponent } from '../../components/NoDataComponent';
import { PaginationComponent } from '../../components/Pagination';
import { BranchListItem } from '../../models/dashboard-models';
import { usePagination } from '../../utils/pagination-hooks';
import { ISelectItems } from './types';
import { RenderImage } from './BranchImage';
export const TableBranches = ({
    headings,
    columns,
    selectedItems,
    setSelectedItems,
    setSelectedBranchItems,
    FetchData,
    selectable = true,
    title,
    itemsPerPage = 10,
    loading = false,
}: {
    headings: string[];
    columns: BranchListItem[];
    selectedItems?: ISelectItems;
    setSelectedItems?: React.Dispatch<React.SetStateAction<ISelectItems>>;
    setSelectedBranchItems?: React.Dispatch<React.SetStateAction<BranchListItem[]>>;
    FetchData: (branch: BranchListItem) => Promise<void>;
    selectable?: boolean;
    title?: string;
    itemsPerPage?: number;
    loading?: boolean;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const pagination = usePagination(itemsPerPage);
    const { currentPage, setCurrentPage, pageSize } = pagination;
    const normalizedSelectedItems = selectedItems ?? {};

    const paginatedColumns = useMemo(() => {
        const pageStart = (currentPage - 1) * pageSize;
        return columns.slice(pageStart, pageStart + pageSize);
    }, [columns, currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [columns, setCurrentPage]);

    const handleSingleSelect = (idText: number, checked: boolean) => {
        if (!setSelectedItems) {
            return;
        }

        const updatedSelectedTableItems = {
            ...normalizedSelectedItems,
            [idText]: checked,
        };

        if (checked) {
            setSelectedItems(updatedSelectedTableItems);
        } else {
            delete updatedSelectedTableItems[idText];
            setSelectedItems(updatedSelectedTableItems);
        }
    };
    const [selectedItemIndex, setSelectedItemsIndex] = useState<number>();

    useEffect(() => {
        setSelectedBranchItems?.(columns.filter((item: any) => normalizedSelectedItems[item.id]));
    }, [columns, normalizedSelectedItems, setSelectedBranchItems]);

    const handleSelectAll = (checked: boolean) => {
        if (!setSelectedItems) {
            return;
        }

        const updatedSelectedItems = { ...normalizedSelectedItems };

        paginatedColumns.forEach((item: any) => {
            if (checked) {
                updatedSelectedItems[item.id] = true;
            } else {
                delete updatedSelectedItems[item.id];
            }
        });

        setSelectedItems(updatedSelectedItems);
    };

    return (
        <>
            {title && <div className='fw-bold mb-2'>{title}</div>}
            {loading ? (
                <div className='d-flex flex-column align-items-center justify-content-center py-5'>
                    <Spinner animation='border' className='mb-2' />
                    <div>{t('loading')}</div>
                </div>
            ) : columns && columns.length > 0 ? (
                <>
                    <Table bordered hover className='w-100 position-relative'>
                        <thead>
                            <tr className='position-relative'>
                                {headings &&
                                    headings.length > 0 &&
                                    headings.map((header, index) => {
                                        return (
                                            <th
                                                key={index}
                                                className='text-center border-top fw-bold single-line-text table-header'
                                            >
                                                {t(`${header}`)}
                                            </th>
                                        );
                                    })}
                                {selectable && (
                                    <th>
                                        <FormCheck
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                handleSelectAll(e.target.checked);
                                            }}
                                            checked={
                                                paginatedColumns.length > 0 &&
                                                paginatedColumns.every((item) => normalizedSelectedItems[item.id])
                                            }
                                            type='checkbox'
                                        />
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedColumns &&
                                paginatedColumns.length > 0 &&
                                paginatedColumns.map((column, index) => {
                                    return (
                                        <tr
                                            key={index}
                                            style={{
                                                backgroundColor: selectedItemIndex === index ? '#c4c5c6' : '',
                                                cursor: selectable ? 'pointer' : 'default',
                                            }}
                                            onClick={async (e) => {
                                                e.stopPropagation();

                                                if (selectable) {
                                                    setSelectedItemsIndex(index);
                                                    const isChecked = !normalizedSelectedItems[column.id];
                                                    handleSingleSelect(column.id, isChecked);
                                                    return;
                                                }

                                                await FetchData(column);
                                            }}
                                        >
                                            <td>
                                                <Suspense fallback={<Spinner animation='border' />}>
                                                    <RenderImage
                                                        branchId={column.id}
                                                        onClick={async () => await FetchData(column)}
                                                    />
                                                </Suspense>
                                                {column.name}
                                            </td>
                                            <td>{column.companyName}</td>
                                            <td>{(Number(column.price) / 100).toFixed(2)}</td>
                                            <td>{dateFormat(column.paymentDate, 'dd-mm-yyyy')}</td>
                                            {selectable && (
                                                <td>
                                                    <FormCheck
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                                            e.stopPropagation();
                                                            handleSingleSelect(column.id, e.target.checked);
                                                        }}
                                                        checked={normalizedSelectedItems[column.id ?? 0] ?? false}
                                                        type='checkbox'
                                                    />
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            <tr style={{ fontWeight: 'bold' }}>
                                <td colSpan={headings.length + (selectable ? 1 : 0)} className={'text-end'}>
                                    {t('total') + ': ' + columns.length}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <div className='d-flex justify-content-center'>
                        <PaginationComponent recordsCount={columns.length} pagination={pagination} />
                    </div>
                </>
            ) : (
                <div className='h-100 d-flex align-items-center w-100 px-2'>
                    <NoDataComponent noDataText={t('branches')}></NoDataComponent>
                </div>
            )}
        </>
    );
};
